import React, { useState, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import ReactApexChart from 'react-apexcharts';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCalendarDays } from '@fortawesome/free-solid-svg-icons';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

const REACT_FE = process.env.REACT_APP_FE_BASE;

function AnalyticsPage() {
    const query = new URLSearchParams(useLocation().search);
    const deviceName = query.get('device');

    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');
    const [data, setData] = useState(null);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [isDownloading, setIsDownloading] = useState(false);


    const contentRef = useRef();
    const handleFetchData = async () => {
        setLoading(true);
        try {
            const res = await axios.post(`${REACT_FE}/api/getDataFromDate`, {
                deviceName,
                fromDate,
                toDate,
            });
            setData(res.data);
            setError('');
        } catch (err) {
            console.error(err);
            setError('Error fetching data');
            setData(null);
        } finally {
            setLoading(false); // Hide loader
        }
    };


    function formatDate(dateString) {
        const date = new Date(dateString);
        const day = date.getDate();
        const month = date.toLocaleString('default', { month: 'long' });
        const year = date.getFullYear();

        const daySuffix =
            day === 1 || day === 21 || day === 31
                ? 'st'
                : day === 2 || day === 22
                    ? 'nd'
                    : day === 3 || day === 23
                        ? 'rd'
                        : 'th';

        return `${day}${daySuffix} ${month} ${year}`;
    }


    const handleDownloadPDF = async () => {
        if (!contentRef.current) return;

        setIsDownloading(true);  // Start loader
        try {
            const canvas = await html2canvas(contentRef.current, {
                scale: 2,
                backgroundColor: '#101419',
                useCORS: true,
            });
            const imgData = canvas.toDataURL('image/png');

            const pdf = new jsPDF('p', 'mm', 'a4');
            const pdfWidth = pdf.internal.pageSize.getWidth();
            const pdfHeight = (canvas.height * pdfWidth) / canvas.width;

            let position = 0;
            pdf.addImage(imgData, 'PNG', 0, position, pdfWidth, pdfHeight);

            if (pdfHeight > pdf.internal.pageSize.getHeight()) {
                let heightLeft = pdfHeight;
                position = -pdf.internal.pageSize.getHeight();

                while (heightLeft > 0) {
                    pdf.addPage();
                    pdf.addImage(imgData, 'PNG', 0, position, pdfWidth, pdfHeight);
                    heightLeft -= pdf.internal.pageSize.getHeight();
                    position -= pdf.internal.pageSize.getHeight();
                }
            }

            pdf.save(`Analytics_${deviceName}_${new Date().toISOString().slice(0, 10)}.pdf`);
        } catch (err) {
            console.error("Error generating PDF:", err);
        } finally {
            setIsDownloading(false);  // Stop loader
        }
    };



    return (
        <div style={{ backgroundColor: '#101419', minHeight: '100vh', color: '#FBFDFE', padding: '20px', fontFamily: 'Arial, sans-serif' }}>
            <p style={{ marginBottom: '20px' }}>Device: <strong>{deviceName}</strong></p>

            <div style={{ display: 'flex', gap: '16px', marginBottom: '20px', flexWrap: 'wrap' }}>
                <div>
                    <label style={{ display: 'block', marginBottom: '4px' }}>From Date:</label>
                    <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} style={{ padding: '5px', borderRadius: '4px', border: '1px solid #ccc' }} />
                </div>

                <div>
                    <label style={{ display: 'block', marginBottom: '4px' }}>To Date:</label>
                    <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} style={{ padding: '5px', borderRadius: '4px', border: '1px solid #ccc' }} />
                </div>

                <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-end', marginTop: 'auto' }}>
                    <button
                        onClick={handleFetchData}
                        style={{
                            padding: '8px 16px',
                            backgroundColor: '#4caf50',
                            border: 'none',
                            color: 'white',
                            borderRadius: '4px',
                            cursor: 'pointer',
                        }}
                    >
                        Fetch Data
                    </button>

                    {data && data.data && data.data.length > 0 && (
                        <button
                            onClick={handleDownloadPDF}
                            className={`download-pdf ${isDownloading ? 'loading-download-pdf' : ''}`}
                            style={{
                                padding: '8px 16px',
                                backgroundColor: '#2196f3',
                                border: 'none',
                                color: 'white',
                                borderRadius: '4px',
                                cursor: isDownloading ? 'default' : 'pointer',
                                position: 'relative',
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                minWidth: '130px',  // ✅ fixed width
                                minHeight: '38px',  // ✅ fixed height
                            }}
                        
                            disabled={isDownloading} // prevent multiple clicks
                        >
                            {isDownloading ? (
                                <div className="spinner"></div>
                            ) : (
                                "Download PDF"
                            )}
                        </button>

                    )}
                </div>


            </div>

            {loading && (
                <div className="loader-container">
                    <div className="loader"></div>
                </div>
            )}

            {error && <p style={{ color: 'red', marginBottom: '20px' }}>{error}</p>}

            <div ref={contentRef}>
                {data && data.data && data.data.map((dayData, idx) => {
                    const dayStart = new Date(`${dayData.date}T00:00:00`).getTime();
                    const dayEnd = new Date(`${dayData.date}T23:59:59`).getTime();

                    return (
                        <div key={idx} style={{ marginBottom: '50px', backgroundColor: '#151c23', padding: '16px', borderRadius: '8px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px', color: '#ccc' }}>
                                <FontAwesomeIcon icon={faCalendarDays} />
                                <h4 style={{ margin: 0 }}>
                                    {formatDate(dayData.date)}
                                </h4>
                            </div>



                            {dayData.readings.length === 0 ? (
                                <p>No readings available.</p>
                            ) : (
                                <>
                                    <ReactApexChart

                                        type="line"
                                        height={300}
                                        series={[
                                            {
                                                name: 'Distance',
                                                data: dayData.readings.map(r => [new Date(r.timestamp).getTime(), r.distance]),
                                            }
                                        ]}
                                        options={{
                                            chart: {
                                                type: 'line',
                                                background: 'transparent',
                                                zoom: {
                                                    enabled: true,
                                                    type: 'x',
                                                    autoScaleYaxis: true
                                                },
                                                toolbar: {
                                                    tools: {
                                                        zoom: true,
                                                        pan: true,
                                                        reset: true,
                                                    }
                                                }
                                            },
                                            stroke: {
                                                curve: 'smooth',
                                                width: 2
                                            },
                                            xaxis: {
                                                type: 'datetime',
                                                min: dayStart,
                                                max: dayEnd,
                                                title: { text: 'Time', style: { color: '#ccc' } },
                                                labels: { style: { colors: '#ccc' } },
                                            },


                                            xaxis: {
                                                type: 'datetime',
                                                min: dayStart,
                                                max: dayEnd,
                                                title: { text: 'Time', style: { color: '#ccc' } },
                                                labels: {
                                                    style: { colors: '#ccc' },
                                                    datetimeUTC: false,
                                                },
                                            },

                                            grid: {
                                                borderColor: '#444'
                                            },
                                            annotations: {
                                                yaxis: [
                                                    {
                                                        y: data.alertLevels.red,
                                                        borderColor: 'red',
                                                        strokeDashArray: 4,
                                                        tooltip: {
                                                            enabled: true,
                                                            formatter: () => `Red Alert: ${data.alertLevels.red} cm`,
                                                        }
                                                    },
                                                    {
                                                        y: data.alertLevels.orange,
                                                        borderColor: 'orange',
                                                        strokeDashArray: 4,
                                                        tooltip: {
                                                            enabled: true,
                                                            formatter: () => `Orange Alert: ${data.alertLevels.orange} cm`,
                                                        }
                                                    },
                                                    {
                                                        y: data.alertLevels.yellow,
                                                        borderColor: 'yellow',
                                                        strokeDashArray: 4,
                                                        tooltip: {
                                                            enabled: true,
                                                            formatter: () => `Yellow Alert: ${data.alertLevels.yellow} cm`,
                                                        }
                                                    },
                                                ]
                                            },

                                            tooltip: {
                                                theme: 'dark',
                                                x: { format: 'HH:mm:ss' }
                                            }
                                        }}
                                    />
                                    <div style={{
                                        marginTop: '16px',
                                        textAlign: 'center',
                                        color: '#ccc',
                                        fontSize: '14px'
                                    }}>
                                        <span style={{ color: 'red', marginRight: '20px' }}>🔴 Red Alert: {data.alertLevels.red} cm</span>
                                        <span style={{ color: 'orange', marginRight: '20px' }}>🟠 Orange Alert: {data.alertLevels.orange} cm</span>
                                        <span style={{ color: 'yellow' }}>🟡 Yellow Alert: {data.alertLevels.yellow} cm</span>
                                    </div>

                                </>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

export default AnalyticsPage;
